import { resolveTz } from './airportTimezones';
import { calculatedDurationMinutes, localToUTCMs } from './tripUtils';
import { timeToMinutes } from './schedulePrefs';

type Clock = { date: string; zone: string; day: string };
type Slot = { start?: string; clock?: Clock; [key: string]: any };
type Journey = { id?: string; dep?: string; arr?: string; depTz?: string; arrTz?: string; fromCode?: string; toCode?: string; from?: string; to?: string; fromCity?: string; toCity?: string };
const midnight = (date: string) => Date.parse(`${date}T00:00:00Z`);

/** Minutes on one elapsed-time axis. Local HH:MM remains the editable/display value. */
export function scheduleMinute(slot: Slot): number {
  if (!slot.clock || !slot.start) return timeToMinutes(slot.start || '');
  return (localToUTCMs(`${slot.clock.date}T${slot.start}`, slot.clock.zone) - midnight(slot.clock.day)) / 60000;
}

export const compareScheduleTime = (a: Slot, b: Slot) => scheduleMinute(a) - scheduleMinute(b);

/** Turn an elapsed-time cursor back into this stop's local date and clock. */
export function shiftScheduleTime<T extends Slot>(slot: T, minute: number): T {
  if (!slot.clock) {
    const m = Math.max(0, Math.min(1439, Math.round(minute)));
    return { ...slot, start: `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}` };
  }
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone: slot.clock.zone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
    .formatToParts(new Date(midnight(slot.clock.day) + minute * 60000));
  const value = (key: string) => parts.find(p => p.type === key)!.value;
  return { ...slot, start: `${value('hour')}:${value('minute')}`, clock: { ...slot.clock, date: `${value('year')}-${value('month')}-${value('day')}` } };
}

/** Stamp before any wall-clock sort. Saved journey titles are the legacy fallback. */
export function stampJourneyClocks<T extends Slot>(slots: T[], day: string, transport: Journey): T[] {
  const depZone = resolveTz(transport.depTz, transport.fromCode, transport.from, transport.fromCity);
  const arrZone = resolveTz(transport.arrTz, transport.toCode, transport.to, transport.toCity);
  if (!depZone || !arrZone || !transport.dep?.includes('T') || !transport.arr?.includes('T')
      || calculatedDurationMinutes(transport, transport.dep, transport.arr, depZone, arrZone) == null) return slots;
  const origin: Clock = { date: transport.dep.slice(0, 10), zone: depZone, day };
  const destination: Clock = { date: transport.arr.slice(0, 10), zone: arrZone, day };
  const belongs = (s: Slot) => !transport.id || !(s.legAfter?.linkedId || s.journeyTransportId)
    || (s.legAfter?.linkedId || s.journeyTransportId) === transport.id;
  const boarding = slots.findIndex(s => belongs(s) && (s.kind === 'board' || s.journeyRole === 'board' || s.legAfter));
  const arrival = slots.findIndex(s => belongs(s) && (s.kind === 'arrive' || s.journeyRole === 'arrive' || (s.journey && /^arriv/i.test(s.title || ''))));
  return slots.map((s, index) => {
    const role = s.kind || s.journeyRole;
    const isArrival = role === 'arrive' || (s.journey && /^arriv/i.test(s.title || ''));
    const fromOrigin = role === 'checkout' || role === 'board' || !!s.legAfter
      || (!isArrival && role !== 'checkin' && s.clockSide !== 'destination' && (
        (s.journey && /^(pack|check[ -]?out)/i.test(s.title || ''))
        || s.clockSide === 'origin'
        || (transport.fromCity && `${s.location || ''} ${s.title || ''}`.toLowerCase().includes(transport.fromCity.toLowerCase()))
        // Only use sequence when the saved array is in journey order, not a
        // legacy wall-clock sort with a westbound arrival before boarding.
        || (day === origin.date && index < boarding && (arrival < 0 || boarding < arrival))));
    const clock = fromOrigin ? origin : isArrival || role === 'checkin' || s.clockSide === 'destination' ? destination : s.clock || destination;
    return { ...s, clock,
      ...(isArrival ? { clockFrom: { start: transport.dep!.slice(11, 16), clock: origin } } : {}) };
  });
}

export function clockChangeLabel(previous: Slot | undefined, current: Slot): string {
  if (!current.clock || !current.start) return '';
  previous ??= current.clockFrom;
  if (!previous?.clock || !previous.start) {
    const days = Math.round((midnight(current.clock.date) - midnight(current.clock.day)) / 86400000);
    return days ? `(${days > 0 ? '+' : '−'}${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'})` : '';
  }
  const offset = (s: Slot) => (Date.parse(`${s.clock!.date}T${s.start}:00Z`) - localToUTCMs(`${s.clock!.date}T${s.start}`, s.clock!.zone)) / 60000;
  const delta = offset(current) - offset(previous);
  const days = Math.round((midnight(current.clock.date) - midnight(previous.clock.date)) / 86400000);
  if (!delta && !days) return '';
  const n = Math.abs(delta);
  const duration = [Math.floor(n / 60) && `${Math.floor(n / 60)}h`, n % 60 && `${n % 60}m`].filter(Boolean).join(' ');
  const change = delta ? `${delta > 0 ? '+' : '−'}${duration} timezone` : '';
  const dayChange = days ? `${days > 0 ? '+' : '−'}${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'}` : '';
  return `(${[change, dayChange].filter(Boolean).join(' · ')})`;
}

/** Read-time compatibility for saved schedules; never rewrites their stored times. */
export function projectScheduleDay<T extends { date: string; slots?: Slot[] }>(day: T, transports: (Journey & { id?: string })[]): T & { slots: Slot[] } {
  let slots = day.slots || [];
  const ids = slots.flatMap(s => [s.legAfter?.linkedId, s.journeyTransportId]).filter(Boolean);
  const linked = transports.filter(t => t.id && ids.includes(t.id));
  const arrivalAt = (t: Journey) => {
    const zone = resolveTz(t.arrTz, t.toCode, t.to, t.toCity);
    return t.arr?.includes('T') && zone ? localToUTCMs(t.arr, zone) : 0;
  };
  linked.sort((a, b) => arrivalAt(a) - arrivalAt(b));
  if (linked.length) {
    const projections = new Map(linked.map(t => [t.id, stampJourneyClocks(slots, day.date, t)]));
    slots = slots.map((s, index) => {
      const sourceId = s.legAfter?.linkedId || s.journeyTransportId;
      const location = `${s.location || ''} ${s.title || ''}`.toLowerCase();
      const transport = linked.find(t => t.id === sourceId)
        || (s.journey && linked.find(t => [t.to, t.toCity].some(place => place && location.includes(place.toLowerCase()))))
        || linked.find(t => {
          const board = slots.findIndex(slot => slot.legAfter?.linkedId === t.id);
          const arrive = slots.findIndex(slot => slot.journeyTransportId === t.id && (slot.journeyRole === 'arrive' || slot.kind === 'arrive'));
          return index < board && (arrive < 0 || board < arrive);
        })
        || linked[linked.length - 1];
      const stamped = projections.get(transport.id)![index];
      const duration = calculatedDurationMinutes(transport, transport.dep, transport.arr, transport.depTz, transport.arrTz);
      return s.legAfter?.linkedId === transport.id && duration != null
        ? { ...stamped, legAfter: { ...s.legAfter, durationMin: duration } } : stamped;
    });
  }
  else {
    // New additions on a stamped day inherit the destination's clock.
    const clock = [...slots].sort(compareScheduleTime).filter(s => s.clock).at(-1)?.clock;
    if (clock) slots = slots.map(s => s.clock ? s : { ...s, clock });
  }
  return { ...day, slots: [...slots].sort(compareScheduleTime) };
}
